import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useAddressSliceActions } from 'feature/addresses/hooks/useAddressSliceActions'
import { useAddressState } from 'feature/addresses/hooks/useAddressState'
import { useScreen } from 'feature/screenMode'
import { applyAddressToMap } from 'feature/registration/heplers/addressHelpers'
import { FormEvent, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useActions, useAppSelector } from 'store/hooks'
import { Button } from 'ui'
import { LoaderInCentalPosition } from 'widget/LoaderInCentalPosition/LoaderInCentalPosition'
import { AddressPopup } from 'feature/addresses/components/AddressPopup/AddressPopup'
import { CompanyDetailsFormValues } from 'feature/company/types/CompanyRegistrationForm'
import { CompanyDataSection } from '../components/CompanyDataSection'
import { LegalAddressSection } from '../components/LegalAddressSection'
import { ActualAddressSection } from '../components/ActualAddressSection'
import { DirectorSection } from '../components/DirectorSection'
import { BankDetailsSection } from '../components/BankDetailsSection'
import { DocumentsSection } from '../components/DocumentsSection'
import styles from '../CompanyRegistration.module.scss'

const SAME_AS_LEGAL_KEY = 'companyRegistrationSameAsLegal'

type CompanyDetailsStepProps = {
  onPrevious: () => void
  onSubmit: () => void
  isLoading?: boolean
}

/**
 * Шаг 2: Реквизиты компании
 * Данные компании, юр./факт. адреса, руководитель, банковские реквизиты
 */
export function CompanyDetailsStep({
  onPrevious,
  onSubmit,
  isLoading,
}: CompanyDetailsStepProps) {
  const [viewMode, setViewMode] = useState<string>('form')
  const [sameAsLegal, setSameAsLegal] = useState(() => {
    const saved = sessionStorage.getItem(SAME_AS_LEGAL_KEY)
    return saved !== null ? saved === 'true' : true
  })
  const screen = useScreen()

  // Стейт юридического адреса (default slice)
  const { isValid: isLegalAddressValid, inputSuggest: legalInputSuggest } =
    useAddressState('default')

  // Только валидность факт. адреса (точечная подписка — не вызывает перерендер при вводе)
  const isCompanyAddressValid = useAppSelector(
    state => state.companyAddress.isValid,
  )

  const {
    formState: { isValid: isValidForm },
    setValue,
    getValues,
    watch,
  } = useFormContext<CompanyDetailsFormValues>()

  const legalAddressWatch = watch('legalAddress')
  const documentsValid = watch('documentsValid' as any) as boolean

  // Экшены для юридического адреса
  const legalActions = useAddressSliceActions('default')
  const companyActions = useAddressSliceActions('company')
  const { setClickMarkCoords, setCenter } = useActions()

  // Валидация: юр. адрес обязателен, факт. — только если не совпадает с юр.
  // Все документы выбранного типа обязательны
  const isValid =
    isLegalAddressValid &&
    (sameAsLegal || isCompanyAddressValid) &&
    isValidForm &&
    !!documentsValid

  // Инициализация: загрузить сохранённый юр. адрес из формы в Redux + карту
  useEffect(() => {
    const address = getValues('legalAddress')
    applyAddressToMap(address, {
      setInputAddressSuggest: legalActions.setInputAddressSuggest,
      setIsValidAddress: legalActions.setIsValidAddress,
      setInputAddress: legalActions.setInputAddress,
      setClickMarkCoords,
    })
    if (address?.geoLat && address?.geoLon) {
      setCenter([Number(address.geoLat), Number(address.geoLon)])
    }
  }, [])

  // Синхронизация юр. адреса из Redux → react-hook-form
  useEffect(() => {
    if (legalInputSuggest && isLegalAddressValid) {
      setValue('legalAddress', legalInputSuggest, { shouldValidate: true })
      if (sameAsLegal) {
        setValue('actualAddress', legalInputSuggest, { shouldValidate: true })
      }
    }
  }, [legalInputSuggest, isLegalAddressValid])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isValid) {
      onSubmit()
    }
  }

  const handleSameAsLegalChange = (checked: boolean) => {
    setSameAsLegal(checked)
    sessionStorage.setItem(SAME_AS_LEGAL_KEY, checked.toString())
    if (checked) {
      const legalAddress = getValues('legalAddress')
      if (legalAddress) {
        setValue('actualAddress', legalAddress, { shouldValidate: true })
      }
    } else {
      setValue('actualAddress', null, { shouldValidate: false })
      companyActions.clearAddress()
      sessionStorage.removeItem('companyInputSuggest')
    }
  }

  if (viewMode === 'popup') {
    return <AddressPopup setViewMode={setViewMode} />
  }

  return (
    <div className={styles.companyRegistration__stepWrapper}>
      <form
        className={styles.companyRegistration__form}
        noValidate
        onSubmit={handleSubmit}
        data-testid='company-reg-details-form'
      >
        <div className={styles.companyRegistration__formHeader}>
          <h1 className='h1'>Реквизиты компании</h1>
          <p className={styles.companyRegistration__stepDescription}>
            Укажите данные компании и банковские реквизиты
          </p>
        </div>

        <div className={styles.companyRegistration__fieldsWrapper}>
          <CompanyDataSection />
          <LegalAddressSection setViewMode={setViewMode} screen={screen} />
          <ActualAddressSection
            sameAsLegal={sameAsLegal}
            onSameAsLegalChange={handleSameAsLegalChange}
            legalAddressWatch={legalAddressWatch}
            setViewMode={setViewMode}
            screen={screen}
          />
          <DirectorSection />
          <BankDetailsSection />
          <DocumentsSection />
        </div>

        <div className={styles.companyRegistration__buttons}>
          <Button
            type='button'
            startIcon={<ChevronLeftIcon />}
            onClick={onPrevious}
            variant='outlined'
            size='medium'
            disabled={isLoading}
            data-testid='company-reg-back-button'
          >
            Назад
          </Button>
          <Button
            type='submit'
            endIcon={<ChevronRightIcon />}
            disabled={!isValid || isLoading}
            size='medium'
            variant='contained'
            data-testid='company-reg-submit-button'
          >
            {isLoading && (
              <LoaderInCentalPosition
                className={styles.companyRegistration__loader}
              />
            )}
            Зарегистрироваться
          </Button>
        </div>
      </form>
    </div>
  )
}
